import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X, MapPin } from "lucide-react";
import { COUNTRIES } from "../lib/regions";

/**
 * Multi-select country picker — used for the `management` role's `allowed_regions`.
 * Returns an array of human-readable country names (e.g. ["Malaysia", "India"]).
 * Matches by name OR alpha-2 code in the search input.
 */
export default function MultiRegionPicker({ value = [], onChange, placeholder = "Add countries…", testId = "multi-region" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);
    useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

    const q = query.trim().toLowerCase();
    const filtered = useMemo(
        () => COUNTRIES.filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q || c.code.toLowerCase().startsWith(q)),
        [q]
    );

    const selected = new Set(value);
    const toggle = (name) => {
        const next = selected.has(name) ? value.filter((v) => v !== name) : [...value, name];
        onChange?.(next);
    };
    const removeOne = (name) => onChange?.(value.filter((v) => v !== name));
    const clearAll = () => onChange?.([]);

    return (
        <div className="relative" ref={wrapRef} data-testid={testId}>
            {/* Selected chips + trigger */}
            <div
                className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm flex flex-wrap items-center gap-1.5 cursor-text min-h-[42px]"
                onClick={() => setOpen(true)}
            >
                {value.length === 0 && <span className="text-zinc-400 px-1">{placeholder}</span>}
                {value.map((name) => (
                    <span
                        key={name}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-zinc-200 text-xs text-zinc-700"
                        data-testid={`${testId}-chip-${name}`}
                    >
                        <MapPin className="h-3 w-3 text-zinc-400" />
                        {name}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeOne(name); }}
                            className="hover:text-red-500"
                            aria-label={`Remove ${name}`}
                        ><X className="h-3 w-3" /></button>
                    </span>
                ))}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                    className="ml-auto text-zinc-500 hover:text-zinc-900 p-0.5"
                    data-testid={`${testId}-trigger`}
                ><ChevronDown className="h-4 w-4" /></button>
            </div>
            {value.length > 0 && (
                <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-2">
                    <span data-testid={`${testId}-count`}>{value.length} selected</span>
                    <span className="text-zinc-300">·</span>
                    <button type="button" onClick={clearAll} className="hover:text-zinc-900" data-testid={`${testId}-clear`}>Clear all</button>
                </div>
            )}

            {/* Popover */}
            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-md shadow-lg" data-testid={`${testId}-menu`}>
                    <div className="p-2 border-b border-zinc-100">
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Type to filter…"
                            className="w-full px-2 py-1.5 bg-zinc-100 border border-zinc-200 rounded text-sm"
                            data-testid={`${testId}-search`}
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-center text-xs text-zinc-500">No match for "{query}"</div>
                        )}
                        {filtered.map((c) => {
                            const isSelected = selected.has(c.name);
                            return (
                                <button
                                    key={c.code}
                                    type="button"
                                    onClick={() => toggle(c.name)}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-zinc-50 ${isSelected ? "bg-zinc-50" : ""}`}
                                    data-testid={`${testId}-opt-${c.code}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span className={`h-3.5 w-3.5 rounded border flex items-center justify-center ${isSelected ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                            {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                        </span>
                                        <span className="text-zinc-900">{c.name}</span>
                                    </span>
                                    <span className="text-[10px] text-zinc-400 font-mono">{c.code}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
